'use client';
import { createContext, useContext, useReducer, useState } from 'react';
import { ChatInferenceRequest, CompletionInferenceRequest } from '~/services/inference/chat';
import type {
  ControlNetOptions,
  ImageGenerationOptions,
  ImageTransformationOptions,
} from '~/services/inference/image-generation';
import type { Model } from '~/services/models.types';

export type RequestBodyAction<
  RequestBodySchema extends Record<string, any>,
  Field extends keyof RequestBodySchema = string,
> = {
  field: Field;
  value: RequestBodySchema[Field];
};

type PlaygroundTabs = 'chat' | 'completion' | 'image' | 'image-to-image' | 'controlNet';

interface RequestContextType {
  model: Model;
  selectedTab: PlaygroundTabs;
  setSelectedTab: React.Dispatch<React.SetStateAction<PlaygroundTabs>>;
  chatState: ChatInferenceRequest; // Define a more specific type based on usage
  completionState: CompletionInferenceRequest; // Define a more specific type based on usage
  imageState: ImageGenerationOptions; // Define a more specific type based on usage
  imageTransformState: ImageTransformationOptions; // Define a more specific type based on usage
  controlNetState: ControlNetOptions; // Define a more specific type based on usage

  setCompletionState: React.Dispatch<React.SetStateAction<any>>; // Adjust the type accordingly
  setImageState: React.Dispatch<React.SetStateAction<any>>; // Adjust the type accordingly
  setChatState: React.Dispatch<React.SetStateAction<any>>; // Adjust the type accordingly
  setImageTransformState: React.Dispatch<React.SetStateAction<any>>; // Adjust the type accordingly
  setControlNetState: React.Dispatch<React.SetStateAction<any>>; // Adjust the type accordingly
}

export const RequestStateContext = createContext<RequestContextType | undefined>(undefined);

export const RequestStateProvider: React.FC<{ children: React.ReactNode; model: Model }> = ({ children, model }) => {
  const [selectedTab, setSelectedTab] = useState<PlaygroundTabs>(model.type === 'text' ? 'chat' : 'image');

  const [chatState, setChatState] = useReducer(
    (state: ChatInferenceRequest, action: RequestBodyAction<ChatInferenceRequest>): ChatInferenceRequest => {
      if (action.field === 'stop') {
        return { ...state, [action.field]: (action.value as string[]).filter((v: string) => v.trim().length > 0) };
      }
      return { ...state, [action.field]: action.value };
    },
    model.generationDefaults.chat,
  );

  const [completionState, setCompletionState] = useReducer(
    (
      state: CompletionInferenceRequest,
      action: RequestBodyAction<CompletionInferenceRequest>,
    ): CompletionInferenceRequest => {
      if (action.field === 'stop') {
        return { ...state, [action.field]: (action.value as string[]).filter((v: string) => v.trim().length > 0) };
      }
      return { ...state, [action.field]: action.value };
    },
    model.generationDefaults.completion,
  );

  const [imageState, setImageState] = useReducer(
    (state: ImageGenerationOptions, action: RequestBodyAction<ImageGenerationOptions>): ImageGenerationOptions => {
      return { ...state, [action.field]: action.value };
    },
    model.generationDefaults.image,
  );

  const [imageTransformState, setImageTransformState] = useReducer(
    (
      state: ImageTransformationOptions,
      action: RequestBodyAction<ImageTransformationOptions>,
    ): ImageTransformationOptions => {
      return { ...state, [action.field]: action.value };
    },
    model.generationDefaults.imageToImage,
  );

  const [controlNetState, setControlNetState] = useReducer(
    (state: ControlNetOptions, action: RequestBodyAction<ControlNetOptions>): ControlNetOptions => {
      return { ...state, [action.field]: action.value };
    },
    model.generationDefaults.controlNet,
  );

  return (
    <RequestStateContext.Provider
      value={{
        model,
        selectedTab,
        setSelectedTab,
        chatState,
        setChatState,
        completionState,
        setCompletionState,
        imageState,
        setImageState,
        imageTransformState,
        setImageTransformState,
        controlNetState,
        setControlNetState,
      }}
    >
      {children}
    </RequestStateContext.Provider>
  );
};

export const useRequestState = (): RequestContextType => {
  const context = useContext(RequestStateContext);
  if (context === undefined) {
    throw new Error('useChatState must be used within a ChatStateProvider');
  }
  return context;
};

export const useActiveTabState = () => {
  const {
    model,
    selectedTab,
    chatState,
    setChatState,
    completionState,
    setCompletionState,
    imageState,
    setImageState,
    imageTransformState,
    setImageTransformState,
    controlNetState,
    setControlNetState,
  } = useRequestState();

  const setRequestState = (state: { field: string | number | symbol; value: any }) => {
    switch (selectedTab) {
      case 'chat':
        setChatState(state);
        break;
      case 'completion':
        setCompletionState(state);
        break;
      case 'image':
        setImageState(state);
        break;
      case 'image-to-image':
        setImageTransformState(state);
        break;
      case 'controlNet':
        setControlNetState(state);
        break;
      default:
        break;
    }
  };

  let requestState;
  switch (selectedTab) {
    case 'chat':
      requestState = chatState;
      break;
    case 'completion':
      requestState = completionState;
      break;
    case 'image':
      requestState = imageState;
      break;
    case 'image-to-image':
      requestState = imageTransformState;
      break;
    case 'controlNet':
      requestState = controlNetState;
      break;
    default:
      requestState = chatState;
      break;
  }

  return {
    model,
    selectedTab,
    requestState,
    setRequestState,
  };
};
